/**
 * Business Users Management Service
 * Modern Firebase-based business employee management
 */

import api from '../shared/services/api';

const ENDPOINTS = {
  GET_USERS: (businessId) => `/api/business-users/${businessId}`,
  GET_USER: (businessId, userId) => `/api/business-users/${businessId}/user/${userId}`,
  CREATE_USER: (businessId) => `/api/business-users/${businessId}`,
  UPDATE_USER: (businessId, userId) => `/api/business-users/${businessId}/user/${userId}`,
  UPDATE_STATUS: (businessId, userId) => `/api/business-users/${businessId}/user/${userId}/status`,
  RESET_PASSWORD: (businessId, userId) => `/api/business-users/${businessId}/user/${userId}/password`,
  DELETE_USER: (businessId, userId) => `/api/business-users/${businessId}/user/${userId}`,
  GET_ROLES: '/api/business-users/roles'
};

export const businessUsersService = {
  /**
   * Get all users for a business
   */
  async getUsers(businessId, options = {}) {
    const { role, status, page = 1, limit = 20, search } = options;

    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (status) params.append('status', status);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);

    const response = await api.get(`${ENDPOINTS.GET_USERS(businessId)}?${params.toString()}`);
    return {
      success: true,
      data: response.data.users || [],
      pagination: response.data.pagination,
      statistics: response.data.statistics
    };
  },

  /**
   * Get single user details
   */
  async getUser(businessId, userId) {
    const response = await api.get(ENDPOINTS.GET_USER(businessId, userId));
    return {
      success: true,
      data: response.data.user
    };
  },

  /**
   * Create new business user
   */
  async createUser(businessId, userData) {
    const response = await api.post(ENDPOINTS.CREATE_USER(businessId), userData);
    return {
      success: true,
      data: {
        ...response.data,
        user: response.data.user,
        alreadyExists: response.data.alreadyExists || false
      },
      message: response.data.message
    };
  },

  /**
   * Update user information
   */
  async updateUser(businessId, userId, updates) {
    const response = await api.put(ENDPOINTS.UPDATE_USER(businessId, userId), updates);
    return {
      success: true,
      message: response.data.message,
      updated_fields: response.data.updated_fields
    };
  },

  /**
   * Update user status (active/inactive/suspended)
   */
  async updateUserStatus(businessId, userId, status, reason = null) {
    const response = await api.put(ENDPOINTS.UPDATE_STATUS(businessId, userId), {
      status,
      reason
    });
    return {
      success: true,
      message: response.data.message || 'Durum başarıyla güncellendi'
    };
  },

  /**
   * Reset user password
   */
  async resetPassword(businessId, userId, newPassword, requireChange = true) {
    const response = await api.put(ENDPOINTS.RESET_PASSWORD(businessId, userId), {
      new_password: newPassword,
      require_change: requireChange
    });
    return response.data;
  },

  /**
   * Delete user (soft or permanent)
   */
  async deleteUser(businessId, userId, permanent = false) {
    const response = await api.delete(ENDPOINTS.DELETE_USER(businessId, userId), {
      data: { permanent_delete: permanent }
    });
    return {
      success: true,
      message: response.data.message || 'Kullanıcı başarıyla silindi'
    };
  },

  /**
   * Get available roles
   */
  async getRoles() {
    // Return static roles for now
    return {
      success: true,
      data: [
        { value: 'owner', label: 'İşletme Sahibi' },
        { value: 'manager', label: 'Müdür' },
        { value: 'employee', label: 'Çalışan' },
        { value: 'cashier', label: 'Kasiyer' },
        { value: 'kitchen_staff', label: 'Mutfak Personeli' },
        { value: 'waiter', label: 'Garson' },
        { value: 'courier', label: 'Kurye' },
        { value: 'cari', label: 'Cari Kullanıcı (B2B)' }
      ]
    };
  },

  /**
   * Generate a secure random password
   */
  generatePassword(length = 12) {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // special char

    // Fill the rest
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  },

  /**
   * Validate user data
   */
  validateUserData(userData) {
    const errors = [];

    if (!userData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      errors.push('Geçerli bir email adresi gerekli');
    }

    if (!userData.first_name || userData.first_name.trim().length < 2) {
      errors.push('Ad en az 2 karakter olmalı');
    }

    if (!userData.last_name || userData.last_name.trim().length < 2) {
      errors.push('Soyad en az 2 karakter olmalı');
    }

    if (!userData.role) {
      errors.push('Rol seçimi gerekli');
    }

    if (userData.password && userData.password.length < 6) {
      errors.push('Şifre en az 6 karakter olmalı');
    }

    if (userData.phone && !/^(\+90|0)?[0-9]{10}$/.test(userData.phone.replace(/\s/g, ''))) {
      errors.push('Geçerli bir telefon numarası girin');
    }

    return errors;
  },

  /**
   * Get user status display info
   */
  getStatusInfo(status) {
    const statusConfig = {
      active: {
        label: 'Aktif',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      inactive: {
        label: 'Pasif',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200'
      },
      suspended: {
        label: 'Askıya Alındı',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      deleted: {
        label: 'Silindi',
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      }
    };

    return statusConfig[status] || statusConfig.inactive;
  },

  /**
   * Get role display info
   */
  getRoleInfo(role) {
    const roleConfig = {
      owner: {
        label: 'İşletme Sahibi',
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        description: 'Tüm yetkilere sahip'
      },
      manager: {
        label: 'Müdür',
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        description: 'İşletme yönetimi yetkileri'
      },
      employee: {
        label: 'Çalışan',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        description: 'Genel çalışan yetkileri'
      },
      cashier: {
        label: 'Kasiyer',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        description: 'POS ve ödeme yetkileri'
      },
      kitchen_staff: {
        label: 'Mutfak Personeli',
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        description: 'Mutfak ve sipariş hazırlama'
      },
      waiter: {
        label: 'Garson',
        color: 'indigo',
        bgColor: 'bg-indigo-100',
        textColor: 'text-indigo-800',
        description: 'Masa servisi ve sipariş alma'
      },
      cari: {
        label: 'Cari Kullanıcı (B2B)',
        color: 'pink',
        bgColor: 'bg-pink-100',
        textColor: 'text-pink-800',
        description: 'B2B Sipariş ve hesap takibi'
      },
      courier: {
        label: 'Kurye',
        color: 'teal',
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-800',
        description: 'Teslimat ve kurye yetkileri'
      }
    };

    return roleConfig[role] || roleConfig.employee;
  },

  /**
   * Format user for display
   */
  formatUserForDisplay(user) {
    return {
      ...user,
      full_name: user.full_name || `${user.first_name} ${user.last_name}`,
      role_info: this.getRoleInfo(user.role),
      status_info: this.getStatusInfo(user.status),
      formatted_created_at: user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '',
      formatted_last_login: user.last_login ? new Date(user.last_login).toLocaleString('tr-TR') : 'Hiç giriş yapmadı',
      initials: user.first_name && user.last_name ?
        `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase() :
        'U'
    };
  },

  /**
   * Search users locally
   */
  searchUsers(users, searchTerm) {
    if (!searchTerm) return users;

    const term = searchTerm.toLowerCase();
    return users.filter(user =>
      user.full_name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.phone?.includes(term) ||
      this.getRoleInfo(user.role).label.toLowerCase().includes(term) ||
      user.department?.toLowerCase().includes(term)
    );
  },

  /**
   * Sort users by different criteria
   */
  sortUsers(users, sortBy = 'name', sortOrder = 'asc') {
    const sorted = [...users].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.full_name || `${a.first_name} ${a.last_name}`;
          bValue = b.full_name || `${b.first_name} ${b.last_name}`;
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login) : new Date(0);
          bValue = b.last_login ? new Date(b.last_login) : new Date(0);
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  },

  /**
   * Get user permissions based on role
   */
  getUserPermissions(role) {
    const permissionMap = {
      // Owner and admin roles - full access
      owner: ['*'],
      admin: ['*'],
      business_accounts: ['*'],
      business_owner: ['*'],
      branch_manager: ['*'],
      işletme_sahibi: ['*'],

      // Manager role - extensive access
      manager: [
        'dashboard_access',
        'pos_access', 'pos_basic', 'pos_advanced',
        'financial_reports', 'reports_view', 'reports_export',
        'kitchen_access', 'kitchen_management',
        'inventory_view', 'inventory_edit',
        'user_view', 'user_manage',
        'settings_basic', 'settings_advanced',
        'customer_management', 'table_manage', 'table_assign',
        'order_management',
        'phone_orders',
        'qr_menu_access',
        'social_media_access',
        'whatsapp_access',
        'scale_integration',
        'technology_access',
        'campaign_access'
      ],

      // Employee role - general access
      employee: [
        'dashboard_access',
        'pos_access', 'pos_basic',
        'order_management',
        'customer_management',
        'table_manage'
      ],

      // Cashier role - POS focused
      cashier: [
        'dashboard_access',
        'pos_access', 'pos_basic', 'pos_advanced',
        'order_management',
        'customer_management',
        'reports_view'
      ],

      // Kitchen staff role - kitchen focused
      kitchen_staff: [
        'dashboard_access',
        'kitchen_access', 'kitchen_management',
        'order_management'
      ],

      // Waiter role - service focused
      waiter: [
        'dashboard_access',
        'pos_access', 'pos_basic',
        'order_management',
        'table_manage', 'table_assign',
        'customer_management'
      ],
      cari: [
        'b2b_order_access',
        'customer_self_view',
        'order_history_view'
      ],
      courier: [
        'courier_app_access',
        'order_management'
      ]
    };

    return permissionMap[role] || [];
  },

  /**
   * Check if user has specific permission
   */
  hasPermission(user, permission) {
    const permissions = this.getUserPermissions(user.role);
    return permissions.includes('*') || permissions.includes(permission);
  }
};

export default businessUsersService;